/*!
 * Copyright (c) Microsoft Corporation.
 * All rights reserved. See LICENSE in the project root for license information.
 */

import { IActionContext } from '@msdyn365-commerce/core';
import {
    InventoryAvailabilitySearchCriteria,
    ProductAvailableQuantity,
    ProductWarehouseInventoryAvailability,
    ProductWarehouseInventoryInformation,
    SearchArea
} from '@msdyn365-commerce/retail-proxy';

export enum DeliveryMode {
    allWareHouses = 0,
    shipping = 1,
    pickup = 2
}

/**
 * This setting defines the inventory levels supported.
 */
export enum InventoryLevels {
    physicalAvailable = 'physicalAvailable',
    totalAvailable = 'totalAvailable',
    threshold = 'inventoryThreshold',
    minOfOrderedInTotalAndTotalAvailable = 'minOfOrderedInTotalAndTotalAvailable'
}

/**
 * Product inventory information class.
 */
export interface IProductInventoryInformation {
    ProductAvailableQuantity: ProductAvailableQuantity;
    StockLevelCode?: string;
    StockLevelLabel?: string;
    IsProductAvailable: boolean;
    InventLocationId?: string;
    deliveryType?: DeliveryMode;
}

/**
 * This setting defines the inventory level values return by the API.
 */
export enum InventoryLevelValues {
    outOfStock = 'OOS',
    available = 'AVAIL'
}
/**
 * The function defines whether the inventory label should be displayed or not.
 * @param context - The action context.
 * @param inventoryCode - The inventory code.
 * @returns Boolean.
 */
export function displayLabelEnabled(context: IActionContext, inventoryCode?: string): boolean {
    if (!inventoryCode) {
        return false;
    }

    // eslint-disable-next-line default-case
    switch (context.requestContext.app.config.inventoryRanges) {
        case 'lowAndOutofStock':
            if (inventoryCode !== InventoryLevelValues.available) {
                return true;
            }
            break;
        case 'outOfStock':
            if (inventoryCode === InventoryLevelValues.outOfStock) {
                return true;
            }
            break;
        case 'all':
            return true;
    }
    return false;
}

/**
 * The function that maps a ProductWareHouse object into a ProductAvailabilityQuantity.
 * @param context - The action context.
 * @param productsWarehouseInventory - The product warehouse inventory information.
 * @returns IProductInventoryInformation[].
 */
export function mapProductInventoryInformation(
    context: IActionContext,
    productsWarehouseInventory?: ProductWarehouseInventoryAvailability[]
): IProductInventoryInformation[] {
    const productInventoryInformation: IProductInventoryInformation[] = [];

    if (context.requestContext.app.config.enableStockCheck === undefined || context.requestContext.app.config.enableStockCheck === false) {
        return productInventoryInformation;
    }

    if (productsWarehouseInventory && productsWarehouseInventory.length > 0) {
        for (const product of productsWarehouseInventory) {
            switch (context.requestContext.app.config.inventoryLevel) {
                case InventoryLevels.physicalAvailable:
                    // Inventory level is enabled with Physical inventory
                    const physicalProductMap = mapInventoryWithPhysicalBufferEnabled(context, product);
                    if (physicalProductMap) {
                        productInventoryInformation.push(physicalProductMap);
                    }
                    break;
                case InventoryLevels.totalAvailable:
                    // Inventory level is enabled with Total available inventory
                    const totalAvailableProductMap = mapInventoryWithTotalAvailableBufferEnabled(context, product);
                    if (totalAvailableProductMap) {
                        productInventoryInformation.push(totalAvailableProductMap);
                    }
                    break;
                case InventoryLevels.minOfOrderedInTotalAndTotalAvailable:
                    // Inventory level is enabled with minimum of ordered in total and total available inventory
                    const minOfOrderedInTotalAndTotalAvailableProductMap = mapInventoryWithMinOfOrderedInTotalAndTotalAvailableEnabled(context, product);
                    if (minOfOrderedInTotalAndTotalAvailableProductMap) {
                        productInventoryInformation.push(minOfOrderedInTotalAndTotalAvailableProductMap);
                    }
                    break;
                default:
                    // When inventory level is inventoryThreshold or not defined
                    const productMap = mapInventoryWithThresholdEnabled(context, product);
                    if (productMap) {
                        productInventoryInformation.push(productMap);
                    }
                    break;
            }
        }
    }

    return productInventoryInformation;
}

/**
 * The function that maps inventory for when minimum of ordered in total and total available is selected.
 * @param context - The action context.
 * @param productWarehouseInventoryAvailability - The product warehouse inventory information.
 * @returns IProductInventoryInformation.
 */
export function mapInventoryWithMinOfOrderedInTotalAndTotalAvailableEnabled(
    context: IActionContext,
    productWarehouseInventoryAvailability: ProductWarehouseInventoryAvailability
): IProductInventoryInformation | null {
    if (
        productWarehouseInventoryAvailability.ProductId
        && (productWarehouseInventoryAvailability.OrderedInTotal !== undefined || productWarehouseInventoryAvailability.TotalAvailable !== undefined)
    ) {
        const productQty = Math.min(...[
            productWarehouseInventoryAvailability.OrderedInTotal,
            productWarehouseInventoryAvailability.TotalAvailable
        ].filter(Boolean) as number[]);
        const productAvailableQuantity: ProductAvailableQuantity = {
            ProductId: productWarehouseInventoryAvailability.ProductId,
            AvailableQuantity: productQty > 0 ? productQty : 0
        };

        return {
            ProductAvailableQuantity: productAvailableQuantity,
            IsProductAvailable: !!(productAvailableQuantity.AvailableQuantity && productAvailableQuantity.AvailableQuantity > 0),
            InventLocationId: productWarehouseInventoryAvailability.InventLocationId
        };
    }

    return null;
}

/**
 * The function that maps inventory for when threshold is selected.
 * @param context - The action context.
 * @param productWarehouseInventoryAvailability - The product warehouse inventory information.
 * @returns IProductInventoryInformation.
 */
export function mapInventoryWithThresholdEnabled(
    context: IActionContext,
    productWarehouseInventoryAvailability: ProductWarehouseInventoryAvailability
): IProductInventoryInformation | null {
    if (productWarehouseInventoryAvailability.ProductId && productWarehouseInventoryAvailability.TotalAvailable !== undefined) {
        const productQty = productWarehouseInventoryAvailability.TotalAvailable - context.requestContext.app.config.outOfStockThreshold;
        const productAvailableQuantity: ProductAvailableQuantity = {
            ProductId: productWarehouseInventoryAvailability.ProductId,
            AvailableQuantity: productQty > 0 ? productQty : 0
        };

        return {
            ProductAvailableQuantity: productAvailableQuantity,
            IsProductAvailable: !!(productAvailableQuantity.AvailableQuantity && productAvailableQuantity.AvailableQuantity > 0),
            InventLocationId: productWarehouseInventoryAvailability.InventLocationId
        };
    }

    return null;
}

/**
 * The function that maps inventory for when physical available is selected.
 * @param context - The action context.
 * @param productWarehouseInventoryAvailability - The product warehouse inventory information.
 * @returns IProductInventoryInformation.
 */
export function mapInventoryWithPhysicalBufferEnabled(
    context: IActionContext,
    productWarehouseInventoryAvailability: ProductWarehouseInventoryAvailability
): IProductInventoryInformation | null {
    const shouldRenderLabel = displayLabelEnabled(context, productWarehouseInventoryAvailability.PhysicalAvailableInventoryLevelCode);

    if (productWarehouseInventoryAvailability.ProductId && productWarehouseInventoryAvailability.TotalAvailable !== undefined) {
        // As per the new buffer logic from RS,
        // if code is out of stock then we set the available quantity to 0 regardless of the actual value from API
        let productQuantity: number | undefined = 0;
        if (productWarehouseInventoryAvailability.PhysicalAvailableInventoryLevelCode !== InventoryLevelValues.outOfStock) {
            productQuantity =
                productWarehouseInventoryAvailability.MaximumPurchasablePhysicalAvailableQuantity ??
                productWarehouseInventoryAvailability.PhysicalAvailable;
        }

        const productAvailableQuantity: ProductAvailableQuantity = {
            ProductId: productWarehouseInventoryAvailability.ProductId,
            AvailableQuantity: productQuantity
        };
        return {
            ProductAvailableQuantity: productAvailableQuantity,
            StockLevelCode: shouldRenderLabel ? productWarehouseInventoryAvailability.PhysicalAvailableInventoryLevelCode : undefined,
            StockLevelLabel: shouldRenderLabel ? productWarehouseInventoryAvailability.PhysicalAvailableInventoryLevelLabel : undefined,
            IsProductAvailable: productAvailableQuantity.AvailableQuantity !== undefined && productAvailableQuantity.AvailableQuantity > 0,
            InventLocationId: productWarehouseInventoryAvailability.InventLocationId
        };
    }

    return null;
}

/**
 * The function that maps inventory for when total available is selected.
 * @param context - The action context.
 * @param productWarehouseInventoryAvailability - The product warehouse inventory information.
 * @returns IProductInventoryInformation.
 */
export function mapInventoryWithTotalAvailableBufferEnabled(
    context: IActionContext,
    productWarehouseInventoryAvailability: ProductWarehouseInventoryAvailability
): IProductInventoryInformation | null {
    const shouldRenderLabel = displayLabelEnabled(context, productWarehouseInventoryAvailability.TotalAvailableInventoryLevelCode);
    if (productWarehouseInventoryAvailability.ProductId && productWarehouseInventoryAvailability.TotalAvailable !== undefined) {
        // As per the new buffer logic from RS,
        // if code is out of stock then we set the available quantity to 0 regardless of the actual value from API
        let productQuantity: number | undefined = 0;
        if (productWarehouseInventoryAvailability.TotalAvailableInventoryLevelCode !== InventoryLevelValues.outOfStock) {
            productQuantity =
                productWarehouseInventoryAvailability.MaximumPurchasableTotalAvailableQuantity ??
                productWarehouseInventoryAvailability.TotalAvailable;
        }

        const productAvailableQuantity: ProductAvailableQuantity = {
            ProductId: productWarehouseInventoryAvailability.ProductId,
            AvailableQuantity: productQuantity
        };
        return {
            ProductAvailableQuantity: productAvailableQuantity,
            StockLevelCode: shouldRenderLabel ? productWarehouseInventoryAvailability.TotalAvailableInventoryLevelCode : undefined,
            StockLevelLabel: shouldRenderLabel ? productWarehouseInventoryAvailability.TotalAvailableInventoryLevelLabel : undefined,
            // eslint-disable-next-line eqeqeq
            IsProductAvailable: productAvailableQuantity.AvailableQuantity != undefined && productAvailableQuantity.AvailableQuantity > 0,
            InventLocationId: productWarehouseInventoryAvailability.InventLocationId
        };
    }

    return null;
}

/**
 * The function created a InventoryAvailabilitySearchCriteria object for getEstimatedAvailability API.
 * @param context - The action context.
 * @returns Boolean.
 */
export function isAggregatedInventory(context: IActionContext): boolean {
    // If warehouse aggregated is true then query inventory from multiple warehouses
    // else query from single warehouse
    return (
        (context.requestContext.app.config.inventoryLevel === InventoryLevels.totalAvailable ||
            context.requestContext.app.config.inventoryLevel === InventoryLevels.physicalAvailable) &&
        context.requestContext.app.config.warehouseAggregation === 'multiple'
    );
}

/**
 * The function created a InventoryAvailabilitySearchCriteria object for getEstimatedAvailability API.
 * @param context - The action context.
 * @param productWarehouseInventoryInformation - The product ware house inventory information.
 * @returns ProductWarehouseInventoryInformation.
 */
export function mapAggregatedProductInventoryInformation(
    context: IActionContext,
    productWarehouseInventoryInformation: ProductWarehouseInventoryInformation
): IProductInventoryInformation[] {
    if (isAggregatedInventory(context)) {
        const productInventoryMapping:
            | ProductWarehouseInventoryAvailability[]
            | undefined = productWarehouseInventoryInformation.AggregatedProductInventoryAvailabilities?.map(product => {
            return {
                DataAreaId: product.DataAreaId,
                MaximumPurchasablePhysicalAvailableQuantity:
                    product.MaximumPurchasablePhysicalAvailableQuantity ?? product.PhysicalAvailableQuantity,
                MaximumPurchasableTotalAvailableQuantity:
                    product.MaximumPurchasableTotalAvailableQuantity ?? product.TotalAvailableQuantity,
                ProductId: product.ProductId,
                PhysicalAvailable: product.PhysicalAvailableQuantity,
                PhysicalAvailableInventoryLevelCode: product.PhysicalAvailableInventoryLevelCode,
                PhysicalAvailableInventoryLevelLabel: product.PhysicalAvailableInventoryLevelLabel,
                TotalAvailable: product.TotalAvailableQuantity,
                TotalAvailableInventoryLevelCode: product.TotalAvailableInventoryLevelCode,
                TotalAvailableInventoryLevelLabel: product.TotalAvailableInventoryLevelLabel
            };
        });
        return mapProductInventoryInformation(context, productInventoryMapping);
    }

    return mapProductInventoryInformation(context, productWarehouseInventoryInformation.ProductWarehouseInventoryAvailabilities);
}

/**
 * The function created a InventoryAvailabilitySearchCriteria object for getEstimatedAvailability API.
 * @param context - The action context.
 * @param productIds - The product id collection.
 * @param isDefaultWareHouse - Flag to define whether inventory is from default warehouse or not.
 * @param isFilterByChannelFulfillmentGroup - Filter by fulfillment group.
 * @param searchArea - The search area.
 * @param defaultDeliveryMode - The delivery mode.
 * @returns InventoryAvailabilitySearchCriteria.
 */
export function createInventoryAvailabilitySearchCriteria(
    context: IActionContext,
    productIds: number[],
    isDefaultWareHouse?: boolean,
    isFilterByChannelFulfillmentGroup?: boolean,
    searchArea?: SearchArea,
    defaultDeliveryMode?: DeliveryMode
): InventoryAvailabilitySearchCriteria {
    const salesUnitType = 2;
    const deliveryMode = defaultDeliveryMode ? defaultDeliveryMode : DeliveryMode.shipping;

    const inventoryAvailabilitySearchCriteria: InventoryAvailabilitySearchCriteria = {
        ProductIds: productIds,
        QuantityUnitTypeValue: salesUnitType,
        SearchArea: searchArea
    };

    if (isAggregatedInventory(context)) {
        inventoryAvailabilitySearchCriteria.FilterByChannelFulfillmentGroup = true;
        inventoryAvailabilitySearchCriteria.DeliveryModeTypeFilterValue = deliveryMode;
    } else {
        inventoryAvailabilitySearchCriteria.DefaultWarehouseOnly = isDefaultWareHouse;
        inventoryAvailabilitySearchCriteria.FilterByChannelFulfillmentGroup = isFilterByChannelFulfillmentGroup;
    }

    return inventoryAvailabilitySearchCriteria;
}
